import React, { useState, useEffect, ComponentType, ReactNode } from 'react';

import {
  fetchWithTokenAndRegion,
  getAdminApiUrl,
  getCurrentToken,
  getCurrentTokenWithoutRedirect,
  getOxygenId,
} from '@adsk/acs-sc-web-platform-api';
import { useSWRAlloy, CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useContextWithValidation,
} from '@adsk/alloy-react-context';
import { useDeprecate } from '@adsk/alloy-react-helpers';

import { Subscription } from './types';

type SubscriptionsContextValue = {
  data: null | Subscription[];
  error: null | unknown;
  loading: boolean;
  retry(): void;
  setEnabled?: (enabled: boolean) => void;
};

type SubscriptionsProviderProps = {
  children?: ReactNode;
};

/**
 * Context for current user subscriptions.
 */
const SubscriptionsContext =
  getOrCreateGlobalContext<SubscriptionsContextValue>(
    CONTEXT_NAMES.SubscriptionsContext,
  );

const useSubscriptionsContext = () =>
  useContextWithValidation(SubscriptionsContext, {
    hook: 'useSubscriptions',
    provider: 'SubscriptionsProvider',
  });

export const useSubscriptions = (enable = true) => {
  useDeprecate(
    true,
    'SubscriptionsContext',
    'useSubscriptions',
    'useSubscriptionsConstruction',
  );
  const { setEnabled, ...rest } = useSubscriptionsContext();
  useEffect(() => {
    if (enable && setEnabled) {
      setEnabled(true);
    }
  }, [setEnabled, enable]);
  return rest;
};

const fetchSubscriptions = () =>
  getCurrentToken().then(({ accessToken }) => {
    const oxygenId = getOxygenId(accessToken);
    return fetchWithTokenAndRegion<{
      results: Subscription[];
    }>(
      `${getAdminApiUrl()}/internal/v1/users/${oxygenId}/subscriptions?filter[status]=active,expired`,
    );
  });

export const SubscriptionsProvider: ComponentType<
  SubscriptionsProviderProps
> = ({ children }) => {
  const [enabled, setEnabled] = useState(false);
  const {
    data: fetchData,
    error,
    revalidate: retry,
  } = useSWRAlloy(enabled ? '/current_user_subscriptions' : null, () =>
    getCurrentTokenWithoutRedirect().then(() => fetchSubscriptions()),
  );
  const data = error?.status === 401 ? null : fetchData;
  const loading = !data && !error;

  return (
    <SubscriptionsContext.Provider
      value={{
        error,
        loading,
        data: data?.results,
        retry,
        setEnabled: enabled ? undefined : setEnabled,
      }}
    >
      {children}
    </SubscriptionsContext.Provider>
  );
};

SubscriptionsProvider.displayName = 'SubscriptionsProvider';
